import { useId, type ReactNode } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import { Icon } from '../../platform/icons/Icon'
import { Money } from '../../platform/primitives/Money'
import { AlertPanel, Panel } from '../../platform/primitives/Panel'
import { oldestOverdue, type Section, type SectionKey } from './listSections'
import type { LocalDate } from './recurringRules'

export const MIXED_NOTE = 'Counts, not totals — this month mixes currencies.'

export const paymentsLabel = (n: number) => `${n} ${n === 1 ? 'payment' : 'payments'}`
export const daysLateLabel = (n: number) => `${n} ${n === 1 ? 'day' : 'days'} late`

const isMixed = (s: Section) => s.totals.length > 1

function Tile({
  label,
  section,
  zeroCurrency,
  alert,
  extra,
}: {
  label: string
  section: Section
  zeroCurrency: string
  alert: boolean
  extra?: string
}) {
  const labelId = useId()
  const mixed = isMixed(section)
  const only = section.totals[0]
  const caption = [mixed ? null : paymentsLabel(section.count), extra ?? null].filter((s): s is string => s !== null)
  const body: ReactNode = (
    <>
      <span className="rec-tile__label">
        {alert && <Icon glyph={AlertTriangle} label="Overdue" size={13} />}
        <span id={labelId}>{label}</span>
      </span>
      <span className="rec-tile__amount">
        {mixed ? (
          paymentsLabel(section.count)
        ) : (
          // A bucket with nothing in it is a true zero, in the household's own currency.
          <Money cents={only?.cents ?? 0} currency={only?.currency ?? zeroCurrency} />
        )}
      </span>
      {caption.length > 0 && <span className="rec-tile__caption">{caption.join(' · ')}</span>}
    </>
  )
  const props = { role: 'group', 'aria-labelledby': labelId, className: `rec-tile${alert ? ' rec-tile--alert' : ''}` }
  // The alert skin means "something is overdue": on a zero it would make the colour lie.
  return alert ? <AlertPanel {...props}>{body}</AlertPanel> : <Panel {...props}>{body}</Panel>
}

/**
 * The three tiles, 3-up at every width (R23). Upcoming, Overdue and Paid in
 * <Month>, each an amount and a count, outflows only (D6). A bucket that mixes
 * currencies shows its count in the amount slot (S12), and the note appears
 * once, below the row.
 */
export function RecurringTiles({
  sections,
  today,
  monthName,
  zeroCurrency,
  compact,
}: {
  sections: Record<SectionKey, Section>
  today: LocalDate
  monthName: string
  zeroCurrency: string
  compact: boolean
}) {
  const late = oldestOverdue(sections.overdue, today)
  const anyMixed = [sections.upcoming, sections.overdue, sections.paid].some(isMixed)
  return (
    <>
      <div className={`rec-tiles${compact ? ' rec-tiles--compact' : ''}`}>
        <Tile label="Upcoming" section={sections.upcoming} zeroCurrency={zeroCurrency} alert={false} />
        <Tile
          label="Overdue"
          section={sections.overdue}
          zeroCurrency={zeroCurrency}
          alert={sections.overdue.count > 0}
          {...(late === null ? {} : { extra: daysLateLabel(late) })}
        />
        <Tile label={`Paid in ${monthName}`} section={sections.paid} zeroCurrency={zeroCurrency} alert={false} />
      </div>
      {anyMixed && (
        <p className="rec-note">
          <Icon glyph={Info} size={13} />
          {MIXED_NOTE}
        </p>
      )}
    </>
  )
}
